import React, { useEffect, useMemo, useState } from 'react';

import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import useRecordMerchantSync from '@hooks/operator/useRecordMerchantSync';
import PageHeader from '@shared/PageHeader';
import { useLoaderData } from 'react-router-dom';

import { inventoryAttributeDefinitionService } from '@services/operator/inventoryAttributeDefinitionService';

import WorkOrderForm from './components/WorkOrderForm';

interface InventoryTypeOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface MerchantInventoryTypeEntry {
  merchant_id: string;
  enabled?: boolean;
  inventory_type?: InventoryTypeOption | null;
}

interface InventoryAttributeDefinition {
  id: string;
  name: string;
  label: string;
  data_type: string;
  required?: boolean;
  options?: unknown;
  [key: string]: unknown;
}

interface WorkOrderLoaderData {
  id: string;
  name: string;
  merchant_id?: string | null;
  merchant_name?: string | null;
  merchant?: { id?: string | null; name?: string | null } | null;
  [key: string]: unknown;
}

const WorkOrderEdit: React.FC = () => {
  const workOrder = useLoaderData() as WorkOrderLoaderData;
  const { handleRecordLoaded, merchantDataLoading } = useRecordMerchantSync();
  const { merchantInventoryTypes, currentMerchant } = useOperatorData() as {
    merchantInventoryTypes?: MerchantInventoryTypeEntry[];
    currentMerchant?: { id?: string; name?: string } | null;
  };

  // Sync 一次：把 record 的 merchant_id 餵給 hook，hook 會在 merchants ready 後切 context。
  // 用 effect 而非直接在 render 內呼叫，避免 strict mode 重複觸發 setState。
  useEffect(() => {
    handleRecordLoaded(workOrder);
  }, [workOrder, handleRecordLoaded]);

  const effectiveMerchantId = currentMerchant?.id ?? null;

  // merchant-scoped reference data：每次 currentMerchant 切換都要重新過濾，wrapper 一手包辦
  // 然後傳給 form。Form 不再直接讀 OperatorDataContext.merchantInventoryTypes，
  // 避免在 edit 模式 race 階段拿到舊 merchant 的 list。
  const inventoryTypes: InventoryTypeOption[] = useMemo(() => {
    if (!effectiveMerchantId) return [];
    return (merchantInventoryTypes ?? [])
      .filter(mit => mit.merchant_id === effectiveMerchantId && mit.enabled !== false)
      .map(mit => mit.inventory_type)
      .filter((it): it is InventoryTypeOption => Boolean(it));
  }, [merchantInventoryTypes, effectiveMerchantId]);

  const [inventoryAttributeDefinitions, setInventoryAttributeDefinitions] =
    useState<InventoryAttributeDefinition[]>([]);

  useEffect(() => {
    if (!effectiveMerchantId) {
      setInventoryAttributeDefinitions([]);
      return;
    }
    let cancelled = false;
    inventoryAttributeDefinitionService
      .fetchList({ merchant_id: effectiveMerchantId })
      .then((result: { success?: boolean; data?: InventoryAttributeDefinition[] }) => {
        if (cancelled) return;
        setInventoryAttributeDefinitions(result?.data ?? []);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Failed to fetch inventory attribute definitions', error);
        setInventoryAttributeDefinitions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveMerchantId]);

  // merchantDataLoading 已涵蓋：global loading + record_merchant_id ≠ currentMerchant.id。
  // 額外擋住 currentMerchant 還沒設好的 first paint（loader resolve 之前）。
  const formMerchantId = effectiveMerchantId ?? '';

  return (
    <div>
      <PageHeader items={['production', 'production.work_orders', { label: workOrder.name }]} />
      <WorkOrderForm
        merchantId={formMerchantId}
        merchant={{ id: currentMerchant?.id, name: currentMerchant?.name }}
        initialWorkOrder={workOrder as Parameters<typeof WorkOrderForm>[0]['initialWorkOrder']}
        inventoryTypes={inventoryTypes}
        inventoryAttributeDefinitions={inventoryAttributeDefinitions}
        merchantDataLoading={merchantDataLoading}
      />
    </div>
  );
};

export default WorkOrderEdit;
